import { Children, isValidElement, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Streamdown } from 'streamdown';

import { Box, Button, Divider, Flex, Link, ListItem, OrderedList, Text, UnorderedList, useBreakpointValue } from '@bonusly/design-system';

import { splitSources } from '../lib/split-sources';
import { bizyTableComponents } from './bizy-markdown-table';

interface BizyStreamingMessageProps {
	/** The content to render */
	content: string;
	/** Whether the response is complete (switches from thinking style to final style) */
	isComplete?: boolean;
	/** Called when content updates (for auto-scroll) */
	onContentUpdate?: () => void;
	/** Tool progress labels shown while tools are running (e.g., ["Reading your notes...", "Working on recognition..."]) */
	toolProgress?: string[];
	/** Rotating thinking phrase shown when no tool progress and not yet streaming content */
	thinkingPhrase?: string;
	/** Called when user clicks a suggested page link (for analytics tracking) */
	onSuggestedPageClick?: (path: string) => void;
}

/**
 * Renders Bizy content as direct markdown (no character-by-character animation).
 *
 * Used by the V2 streaming component for BOTH in-progress and completed messages
 * to avoid the jarring flash that occurs when switching between different component
 * types on completion. The same component handles both states:
 *
 * - Streaming (isComplete=false): italic text, "Thinking..." label, pulsing cursor
 * - Complete (isComplete=true): normal text, no label, no cursor
 */
export function BizyStreamingMessage({
	content,
	isComplete = false,
	onContentUpdate,
	toolProgress,
	thinkingPhrase,
	onSuggestedPageClick,
}: BizyStreamingMessageProps) {
	const { t } = useTranslation('admin_bizy');
	const navigate = useNavigate();
	const prevContentLengthRef = useRef(content.length);

	useEffect(() => {
		if (content.length !== prevContentLengthRef.current) {
			prevContentLengthRef.current = content.length;
			onContentUpdate?.();
		}
	}, [content, onContentUpdate]);

	const textColor = isComplete ? 'text.default' : 'text.softer';
	const bodyFontSize = useBreakpointValue({ base: 'md', sm: 'sm' }) as 'md' | 'sm' | undefined;
	const { body, sources } = useMemo(() => splitSources(content), [content]);

	const markdownSx = useMemo(
		() => ({
			'p + p': { marginTop: '0.5em' },
			'ul, ol': { marginTop: '0.5em', marginBottom: '0.5em', paddingLeft: '4px' },
			'li + li': { marginTop: '8px' },
			'p:first-of-type': { marginTop: 0 },
			'p:last-of-type': { marginBottom: 0 },
			hr: { marginTop: '0.75em', marginBottom: '0.5em', borderColor: 'border.soft' },
		}),
		[]
	);

	const markdownComponents = useMemo(
		() => ({
			a: ({ href, children }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
				const isSameTab = href?.includes('_target=self');
				const cleanHref = isSameTab
					? href
							?.replace(/[?&]_target=self/, '')
							.replace(/\?$/, '')
							.replace(/^([^?]*?)&/, '$1?')
					: href;

				if (isSameTab && cleanHref && cleanHref.startsWith('/') && !cleanHref.startsWith('//')) {
					return (
						<Link
							href={cleanHref}
							color="text.interactive"
							onClick={(e: React.MouseEvent) => {
								e.preventDefault();
								onSuggestedPageClick?.(cleanHref);
								navigate(cleanHref);
							}}
						>
							{children}
						</Link>
					);
				}

				return (
					<Link href={cleanHref} color="text.interactive" {...(!isSameTab && { target: '_blank', rel: 'noopener noreferrer' })}>
						{children}
					</Link>
				);
			},
			p: ({ children }: React.HTMLAttributes<HTMLParagraphElement>) => (
				<Text fontSize={bodyFontSize} color="inherit">
					{children}
				</Text>
			),
			h1: ({ children }: React.HTMLAttributes<HTMLHeadingElement>) => (
				<Text fontSize="md" fontWeight="semibold" color="inherit">
					{children}
				</Text>
			),
			h2: ({ children }: React.HTMLAttributes<HTMLHeadingElement>) => (
				<Text fontSize="md" fontWeight="semibold" color="inherit">
					{children}
				</Text>
			),
			h3: ({ children }: React.HTMLAttributes<HTMLHeadingElement>) => (
				<Text fontSize="md" fontWeight="semibold" color="inherit">
					{children}
				</Text>
			),
			strong: ({ children }: React.HTMLAttributes<HTMLElement>) => {
				const childArray = Children.toArray(children);
				if (childArray.length === 1 && isValidElement(childArray[0])) {
					const child = childArray[0] as React.ReactElement<any>;
					const childType = child.type;
					const isLink = typeof childType === 'function' || typeof childType === 'object' ? child.props?.href !== undefined : childType === 'a';

					if (isLink && child.props?.href) {
						const href = child.props.href as string;
						const isSameTab = href.includes('_target=self');
						const cleanHref = isSameTab
							? href
									.replace(/[?&]_target=self/, '')
									.replace(/\?$/, '')
									.replace(/^([^?]*?)&/, '$1?')
							: href;

						if (isSameTab && cleanHref.startsWith('/') && !cleanHref.startsWith('//')) {
							return (
								<Button
									size="sm"
									variant="outline"
									colorScheme="secondary"
									borderRadius={10}
									my={1}
									onClick={() => {
										onSuggestedPageClick?.(cleanHref);
										navigate(cleanHref);
									}}
								>
									{child.props.children}
								</Button>
							);
						}
					}
				}
				return (
					<Text as="strong" fontSize="inherit" fontWeight="semibold" color="inherit" display="inline">
						{children}
					</Text>
				);
			},
			ol: ({ children }: React.OlHTMLAttributes<HTMLOListElement>) => (
				<OrderedList fontSize={bodyFontSize} color="inherit">
					{children}
				</OrderedList>
			),
			ul: ({ children }: React.HTMLAttributes<HTMLUListElement>) => (
				<UnorderedList fontSize={bodyFontSize} color="inherit">
					{children}
				</UnorderedList>
			),
			li: ({ children }: React.LiHTMLAttributes<HTMLLIElement>) => <ListItem>{children}</ListItem>,
			...bizyTableComponents(bodyFontSize || 'sm'),
			pre: ({ children }: React.HTMLAttributes<HTMLPreElement>) => <pre style={{ whiteSpace: 'pre-wrap', fontSize: 'inherit' }}>{children}</pre>,
			code: ({ children }: React.HTMLAttributes<HTMLElement>) => (
				<Text as="code" fontSize={bodyFontSize} bg="bg.muted" px={1} borderRadius="sm">
					{children}
				</Text>
			),
		}),
		[bodyFontSize, navigate, onSuggestedPageClick]
	);

	return (
		<Box alignSelf="flex-start" maxWidth="100%" pl={1} pr={3}>
			{/* Progress labels for streaming state — shows accumulated tool progress or "Thinking..." */}
			{!isComplete && (
				<Flex direction="column" gap={0.5} mb={1}>
					{toolProgress && toolProgress.length > 0 ? (
						toolProgress.map((item, index) => {
							const isLatest = index === toolProgress.length - 1;
							return (
								<Text
									key={index}
									fontSize="xs"
									fontWeight="medium"
									color="text.softest"
									sx={
										isLatest
											? {
													'@keyframes pulse': {
														'0%, 100%': { opacity: 1 },
														'50%': { opacity: 0.4 },
													},
													animation: 'pulse 1.5s ease-in-out infinite',
												}
											: { opacity: 0.6 }
									}
								>
									{item}
								</Text>
							);
						})
					) : (
						<Text
							fontSize="xs"
							fontWeight="medium"
							color="text.softest"
							sx={{
								'@keyframes pulse': {
									'0%, 100%': { opacity: 1 },
									'50%': { opacity: 0.4 },
								},
								animation: 'pulse 1.5s ease-in-out infinite',
							}}
						>
							{thinkingPhrase ?? t('thinking')}
						</Text>
					)}
				</Flex>
			)}

			<Box color={textColor} transition="color 0.3s ease" sx={markdownSx}>
				<Streamdown components={markdownComponents}>{body}</Streamdown>
			</Box>

			{sources && (
				<Box mt={3}>
					<Divider borderColor="border.soft" />
					<Box mt={2} color="text.softer" fontStyle="normal" sx={{ 'p:first-of-type': { marginTop: 0 }, 'p:last-of-type': { marginBottom: 0 } }}>
						<Streamdown
							components={
								{
									a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
										<Link href={href} color="text.interactive" fontSize="xs" target="_blank" rel="noopener noreferrer">
											{children}
										</Link>
									),
									p: ({ children }: { children?: React.ReactNode }) => (
										<Text fontSize="xs" color="text.softer">
											{children}
										</Text>
									),
								} as Parameters<typeof Streamdown>[0]['components']
							}
						>
							{sources}
						</Streamdown>
					</Box>
				</Box>
			)}
		</Box>
	);
}
